import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, Loader2 } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { z } from 'zod';
import { toast } from 'sonner';
import { BrandVariant, COMPANY_INFO } from '@/lib/brandConfig';
import { submitLead } from '@/lib/leads';
import { trackFormSubmission } from '@/lib/analytics';
import { triggerCelebration } from '@/lib/celebrationEffects';
import { useQuoteModal } from '@/contexts/QuoteModalContext';
import { cn } from '@/lib/utils';
import { 
  PremiumModalShell, 
  premiumInputStyles, 
  getBrandFocusRing,
  getPrimaryButtonStyles 
} from '@/components/modals/PremiumModalShell';

// Form validation schema
const quoteSchema = z.object({
  fullName: z.string().trim().min(2, "Name must be at least 2 characters").max(100),
  phone: z.string().trim().min(10, "Please enter a valid phone number").max(20),
  email: z.string().trim().email("Please enter a valid email").max(255),
  zipCode: z.string().trim().min(5, "Please enter a valid zip code").max(10),
  category: z.string().min(1, "Please select what you're shopping for"),
  message: z.string().trim().max(1000).optional(),
});

type QuoteFormData = z.infer<typeof quoteSchema>;

// Category options (BBQ only)
const CATEGORY_OPTIONS: string[] = ['BBQ Island', 'Outdoor Kitchen', 'Pizza Oven', 'Pergola', 'Fire Features', 'Other'];

// Brand-specific content (BBQ only)
const BRAND_CONTENT = {
  title: "Request Your Quote",
  subtitle: "Tell us about your outdoor kitchen dreams and we'll prepare your personalized quote.",
  successEmoji: "🔥",
  successTitle: "You're All Set!",
};

interface QuoteModalProps {
  siteKey: BrandVariant;
}

export const QuoteModal = ({ siteKey }: QuoteModalProps) => {
  const { isOpen, closeQuoteModal, preselectedCategory, buttonLabel } = useQuoteModal();
  const shouldReduceMotion = useReducedMotion();

  const [step, setStep] = useState<'form' | 'success'>('form');
  const [formData, setFormData] = useState<Partial<QuoteFormData>>({
    fullName: '',
    phone: '',
    email: '',
    zipCode: '',
    category: preselectedCategory,
    message: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof QuoteFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens with new category
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({ ...prev, category: preselectedCategory || prev.category }));
      setStep('form');
      setErrors({});
    }
  }, [isOpen, preselectedCategory]);

  const handleChange = (field: keyof QuoteFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      const validated = quoteSchema.parse(formData);
      
      const result = await submitLead(siteKey, {
        email: validated.email,
        phone: validated.phone,
        name: validated.fullName,
        zip: validated.zipCode,
        category: validated.category,
        message: validated.message,
        sourcePage: `${window.location.pathname}|btn:${buttonLabel}|cat:${validated.category}`,
        leadType: 'quote',
      });

      if (!result.success) {
        toast.error("Something went wrong. Please try again or call us directly.");
        setIsSubmitting(false);
        return;
      }

      trackFormSubmission(siteKey, 'quote_modal');
      triggerCelebration(siteKey);
      setStep('success');
      toast.success("Quote request submitted!");
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Partial<Record<keyof QuoteFormData, string>> = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof QuoteFormData;
          fieldErrors[field] = err.message;
        });
        setErrors(fieldErrors);
        toast.error("Please fix the errors in the form");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    closeQuoteModal();
    setTimeout(() => {
      setStep('form');
      setFormData({
        fullName: '',
        phone: '',
        email: '',
        zipCode: '',
        category: '',
        message: '',
      });
      setErrors({});
    }, 300);
  };

  const inputClasses = cn(premiumInputStyles, getBrandFocusRing(siteKey));

  return (
    <PremiumModalShell
      isOpen={isOpen}
      onClose={handleClose}
      brand={siteKey}
      maxWidth="2xl"
    >
      <AnimatePresence mode="wait">
        {step === 'form' ? (
          <motion.div
            key="form"
            initial={shouldReduceMotion ? {} : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={shouldReduceMotion ? {} : { opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="h-full flex flex-col"
          >
            {/* Header */}
            <div className="mb-6">
              <h2 className="text-2xl sm:text-3xl font-medium text-neutral-800 tracking-tight">
                {BRAND_CONTENT.title}
              </h2>
              <p className="mt-2 text-neutral-500 leading-relaxed">
                {BRAND_CONTENT.subtitle}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
              <div className="space-y-4 flex-1">
                {/* Full Name */}
                <div className="space-y-1.5">
                  <Label htmlFor="fullName" className="text-sm font-medium text-neutral-700">
                    Full Name
                  </Label>
                  <Input
                    id="fullName"
                    placeholder="Your name"
                    value={formData.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    className={cn(inputClasses, errors.fullName && "border-red-300 focus:ring-red-500/30")}
                  />
                  {errors.fullName && <p className="text-red-500 text-xs">{errors.fullName}</p>}
                </div>

                {/* Phone & Email Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="phone" className="text-sm font-medium text-neutral-700">
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className={cn(inputClasses, errors.phone && "border-red-300 focus:ring-red-500/30")}
                    />
                    {errors.phone && <p className="text-red-500 text-xs">{errors.phone}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-sm font-medium text-neutral-700">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={cn(inputClasses, errors.email && "border-red-300 focus:ring-red-500/30")}
                    />
                    {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
                  </div>
                </div>

                {/* Zip Code & Category Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="zipCode" className="text-sm font-medium text-neutral-700">
                      Zip Code
                    </Label>
                    <Input
                      id="zipCode"
                      placeholder="75001"
                      value={formData.zipCode}
                      onChange={(e) => handleChange('zipCode', e.target.value)}
                      className={cn(inputClasses, errors.zipCode && "border-red-300 focus:ring-red-500/30")}
                    />
                    {errors.zipCode && <p className="text-red-500 text-xs">{errors.zipCode}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-neutral-700">I'm interested in</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => handleChange('category', value)}
                    >
                      <SelectTrigger className={cn(inputClasses, "text-left", errors.category && "border-red-300")}>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-neutral-200 shadow-lg rounded-lg">
                        {CATEGORY_OPTIONS.map((cat) => (
                          <SelectItem key={cat} value={cat} className="hover:bg-neutral-50">
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.category && <p className="text-red-500 text-xs">{errors.category}</p>}
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-1.5">
                  <Label htmlFor="message" className="text-sm font-medium text-neutral-700">
                    Additional details <span className="text-neutral-400 font-normal">(optional)</span>
                  </Label>
                  <Textarea
                    id="message"
                    placeholder="Tell us more about what you're looking for..."
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    rows={3}
                    className={cn(
                      "resize-none bg-neutral-50 border border-neutral-200 rounded-lg",
                      "text-neutral-800 placeholder:text-neutral-400",
                      "focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent",
                      getBrandFocusRing(siteKey)
                    )}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="mt-6 space-y-3">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className={getPrimaryButtonStyles(siteKey)}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </Button>

                <p className="text-xs text-center text-neutral-400">
                  Your information is secure and will never be shared.
                </p>
              </div>
            </form>
          </motion.div>
        ) : (
          <motion.div
            key="success"
            initial={shouldReduceMotion ? {} : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-center justify-center text-center py-8 min-h-[400px]"
          >
            <motion.div
              initial={shouldReduceMotion ? {} : { scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
              className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mb-6"
            >
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </motion.div>
            
            <h2 className="text-2xl sm:text-3xl font-medium text-neutral-800 mb-3">
              {BRAND_CONTENT.successEmoji} {BRAND_CONTENT.successTitle}
            </h2>
            <p className="text-neutral-500 max-w-sm leading-relaxed">
              We'll review your request and get back to you within 24 hours with a personalized quote.
            </p>

            <div className="mt-8 p-4 bg-neutral-50 rounded-lg text-center">
              <p className="text-sm text-neutral-500">Need faster assistance?</p>
              <a 
                href={`tel:${COMPANY_INFO.phone.replace(/\D/g, '')}`}
                className="text-lg font-medium text-neutral-800 hover:underline"
              >
                {COMPANY_INFO.phone}
              </a>
            </div>

            <Button
              onClick={handleClose}
              className={cn(getPrimaryButtonStyles(siteKey), "mt-6 max-w-xs")}
            >
              Done
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </PremiumModalShell>
  );
};

export default QuoteModal;
